import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PromptsTable } from '@/components/analytics/PromptsTable';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { AnalyticsFilters, AnalyticsOverview, Granularity } from '@/hooks/useAnalytics';
import {
  COLORS,
  calcTickInterval,
  customTooltipItemStyle,
  customTooltipLabelStyle,
  customTooltipStyle,
  fillTimeSeriesGaps,
  formatDateLabel,
  formatNumber,
  getGranularityLabel,
} from './chart-utils';

interface ChartsSectionProps {
  analyticsData: AnalyticsOverview;
  dateFrom: Date;
  dateTo: Date;
  granularity: Granularity;
  filters: AnalyticsFilters;
}

export const ChartsSection = ({ analyticsData, dateFrom, dateTo, granularity, filters }: ChartsSectionProps) => {
  const granularityLabel = getGranularityLabel(granularity);

  const filledDocuments = useMemo(() =>
    fillTimeSeriesGaps(
      analyticsData.documents_over_time,
      dateFrom,
      dateTo,
      { value: 0 },
      granularity
    ),
    [analyticsData, dateFrom, dateTo, granularity]
  );

  const filledTokens = useMemo(() =>
    fillTimeSeriesGaps(
      analyticsData.tokens_over_time,
      dateFrom,
      dateTo,
      { document_tokens: 0, chat_tokens: 0, eca_tokens: 0, total: 0 },
      granularity
    ),
    [analyticsData, dateFrom, dateTo, granularity]
  );

  const filledMessages = useMemo(() =>
    fillTimeSeriesGaps(
      analyticsData.messages_over_time,
      dateFrom,
      dateTo,
      { user_messages: 0, assistant_messages: 0, total: 0 },
      granularity
    ),
    [analyticsData, dateFrom, dateTo, granularity]
  );

  const documentsChartData = useMemo(() =>
    filledDocuments.map(point => ({
      date: formatDateLabel(point.date, granularity),
      'Documents': point.value,
    })),
    [filledDocuments, granularity]
  );

  const tokensChartData = useMemo(() =>
    filledTokens.map(point => ({
      date: formatDateLabel(point.date, granularity),
      'Document Processing': point.document_tokens,
      'Case Partner': point.chat_tokens,
      'Early Case Assessment': point.eca_tokens,
    })),
    [filledTokens, granularity]
  );

  const messagesChartData = useMemo(() =>
    filledMessages.map(point => ({
      date: formatDateLabel(point.date, granularity),
      'User Messages': point.user_messages,
    })),
    [filledMessages, granularity]
  );

  const tokensTickInterval = useMemo(
    () => calcTickInterval(tokensChartData.length),
    [tokensChartData.length]
  );

  return (
    <div className="space-y-6">
      {/* Messages & Documents Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Messages Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Case Partner Messages</CardTitle>
            <CardDescription>{granularityLabel} user message volume</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={messagesChartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#E5E7EB"
                    strokeOpacity={0.5}
                  />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} allowDecimals={false} />
                  <Tooltip
                    formatter={(value) => Number(value).toLocaleString()}
                    contentStyle={customTooltipStyle}
                    labelStyle={customTooltipLabelStyle}
                    itemStyle={customTooltipItemStyle}
                    cursor={false}
                  />
                  <Bar
                    dataKey="User Messages"
                    fill={COLORS.charts.blue}
                    radius={[4, 4, 0, 0]}
                    animationDuration={800}
                    animationEasing="ease-out"
                    activeBar={{
                      strokeWidth: 1,
                      stroke: COLORS.charts.blueDark,
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Documents Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Documents Uploaded</CardTitle>
            <CardDescription>{granularityLabel} document upload volume</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={documentsChartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#E5E7EB"
                    strokeOpacity={0.5}
                  />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} allowDecimals={false} />
                  <Tooltip
                    formatter={(value) => Number(value).toLocaleString()}
                    contentStyle={customTooltipStyle}
                    labelStyle={customTooltipLabelStyle}
                    itemStyle={customTooltipItemStyle}
                    cursor={false}
                  />
                  <Bar
                    dataKey="Documents"
                    fill={COLORS.charts.blue}
                    radius={[4, 4, 0, 0]}
                    animationDuration={800}
                    animationEasing="ease-out"
                    activeBar={{
                      strokeWidth: 1,
                      stroke: COLORS.charts.blueDark,
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Token Usage Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Token Usage</CardTitle>
          <CardDescription>{granularityLabel} AI token consumption by feature</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={tokensChartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#E5E7EB"
                  strokeOpacity={0.5}
                />
                <XAxis
                  dataKey="date"
                  fontSize={12}
                  interval={tokensTickInterval}
                  textAnchor="end"
                  height={60}
                />
                <YAxis fontSize={12} tickFormatter={formatNumber} allowDecimals={false} />
                <Tooltip
                  formatter={(value) => formatNumber(Number(value))}
                  contentStyle={customTooltipStyle}
                  labelStyle={customTooltipLabelStyle}
                  itemStyle={customTooltipItemStyle}
                  cursor={false}
                />
                <Legend />
                <Bar
                  dataKey="Document Processing"
                  stackId="1"
                  fill={COLORS.charts.tokenPink}
                  radius={[0, 0, 0, 0]}
                  animationDuration={800}
                  animationEasing="ease-out"
                  activeBar={{
                    strokeWidth: 1,
                    stroke: COLORS.charts.tokenPinkActive,
                  }}
                />
                <Bar
                  dataKey="Case Partner"
                  stackId="1"
                  fill={COLORS.charts.tokenBlue}
                  radius={[0, 0, 0, 0]}
                  animationDuration={800}
                  animationEasing="ease-out"
                  activeBar={{
                    strokeWidth: 1,
                    stroke: COLORS.charts.tokenBlueActive,
                  }}
                />
                <Bar
                  dataKey="Early Case Assessment"
                  stackId="1"
                  fill={COLORS.charts.tokenYellow}
                  radius={[4, 4, 0, 0]}
                  animationDuration={800}
                  animationEasing="ease-out"
                  activeBar={{
                    strokeWidth: 1,
                    stroke: COLORS.charts.tokenYellowActive,
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* User Prompts Table */}
      <PromptsTable filters={filters} />
    </div>
  );
};
